import {
    GridPresetState, GridSlotState, LegacyGridKeys, readBaseline, readLegacySlot, writeBaseline,
} from '../presets/grid-preset-storage';

/**
 * One-shot cleanup of the keys this app wrote before every presets host kept a single payload.
 *
 * Two jobs, and the difference matters:
 *
 * - **migrate** — a key still holding the only copy of someone's layout. Its content is written forward
 *   into the host's payload, read back to prove the write landed, and only then removed. A host also
 *   reads its own legacy keys when its payload is missing, so a sweep that fails here costs nothing:
 *   the page still opens on the right layout, and the next reload tries again.
 * - **drop** — a key nothing has read for releases. Removed outright.
 *
 * What this deliberately does NOT do is delete by exclusion from an allowlist. Unknown keys are left
 * alone: every one of them is a feature someone forgot to write down, and a sweep is the worst possible
 * place to find that out. The keep-list below exists only to protect live keys that happen to match one
 * of the dead PREFIXES.
 *
 * Runs once per browser, marked by {@link SWEEP_KEY}, from the module body of `app.config.ts` — before
 * any component field initializer reads a key.
 */

/**
 * Bumped when a later release adds work here; a browser that already ran version N skips it.
 *
 * All four jobs below ship as version 1: they were written across one unreleased branch, so no browser
 * has ever recorded an earlier number. Bump only for work added *after* a release.
 */
const SWEEP_KEY = 'ims:storage-migration';
const SWEEP_VERSION = 1;

/** One presets host and the keys its grids used to be spread across. */
interface GridHostMigration {
    /** Prefix of the host's three keys, i.e. what `configure({storageKey})` was given. */
    storageKey: string;
    /** The legacy pair of every grid the host registers, keyed by the slot id it registers under. */
    slots: Record<string, LegacyGridKeys>;
}

/** `<page>-table:state` + `-table:columns`, the pair every list page was built on. */
function tablePair(prefix: string): LegacyGridKeys {
    return {state: `${prefix}-table:state`, columns: `${prefix}-table:columns`};
}

function singleGrid(storageKey: string, legacyPrefix = storageKey): GridHostMigration {
    return {storageKey, slots: {main: tablePair(legacyPrefix)}};
}

const GRID_HOSTS: GridHostMigration[] = [
    singleGrid('oms:arbitrages'),
    singleGrid('oms:sellers'),
    singleGrid('oms:makers'),
    singleGrid('oms:hedgers'),
    singleGrid('oms:rollers'),
    singleGrid('oms:optimizers'),
    singleGrid('oms:transfers'),
    singleGrid('oms:mt-options-arbitrages'),
    singleGrid('oms:tt-options-arbitrages'),
    singleGrid('oms:options-arbitrages'),
    // The options page named its column key by hand, long before the `-table:columns` convention.
    {storageKey: 'oms:options', slots: {main: {state: 'oms:options-table:state', columns: 'options-visible-columns'}}},
    // The alarms trio: the backend `component` is unprefixed and stays that way, so the legacy keys
    // carry the bare name while the payload lives under `pms:`.
    singleGrid('pms:alarms', 'alarms'),
    singleGrid('pms:deleted-alarms', 'deleted-alarms'),
    singleGrid('pms:risk-rules', 'risk-rules'),
    // The RMS balances table: one grid, no chooser (its `hide` flags ride in the captured state), so
    // there is no columns key to carry.
    {storageKey: 'rms:balances', slots: {main: {state: 'rms:balances-table:state'}}},
    // funding is one host over six tables, which is exactly what a single payload is for. None of them
    // had a chooser, so there is no columns key to carry.
    {
        storageKey: 'oms:funding',
        slots: {
            'offers': {state: 'oms:funding:offers-table:state'},
            'offers-history': {state: 'oms:funding:offers-history-table:state'},
            'taken': {state: 'oms:funding:taken-table:state'},
            'provided': {state: 'oms:funding:provided-table:state'},
            'matched': {state: 'oms:funding:matched-table:state'},
            'book': {state: 'oms:funding:book-table:state'},
        },
    },
];

/**
 * The PMS Dashboards, whose baseline sat on the bare namespace `pms:<name>`.
 *
 * Superseded, and deliberately not extended. A Dashboard's keys now carry the desk
 * (`pms:stop:options:baseline`, see `dashboardPresetName`), so the rename below targets a key no host
 * reads any more; it is kept only because this sweep has already run on every client and rewriting a
 * done migration changes nothing. `nav` was never in the list — it shipped after the sweep — and
 * adding it now would be a rename that can never fire, which is why it stays out. The whole block
 * goes with the read-forward fallbacks (`src/app/shared/CONTEXT.md`, "Still owed").
 */
const PMS_DASHBOARDS = [
    'adjustments', 'balances', 'defi', 'exposure', 'fees', 'funding', 'loans', 'margin',
    'options-volatility', 'prices', 'stop',
];

/** Which bucket a renamed key lives in. A few are written to both — see `funds.service.ts`. */
type Bucket = 'local' | 'session';

interface RenamedKey {
    from: string;
    to: string;
    /** Defaults to localStorage only. */
    buckets?: Bucket[];
}

/**
 * Keys whose content is still wanted, under a name that follows the scheme. Copied if the destination
 * is free, then removed — same read-back discipline as everything else here.
 *
 * A plain rename is safe to do at bootstrap, unlike the folds a page reads forward itself: nothing has
 * to be mounted for a key to change name.
 */
const RENAMED_KEYS: RenamedKey[] = [
    // dashboard-cex's pair selection. It lost the `dashboard-cex-` prefix it shared with that page's
    // dead per-field keys, which is exactly what used to make it look like a leftover — it needed a
    // keep-list entry to survive the sweep that collected its neighbours.
    {from: 'dashboard-cex-selection', to: 'oms:dashboard-cex:selection'},
    // A PMS baseline sat on the bare namespace, so it was both the payload and the prefix of
    // `:working` / `:loaded-preset`.
    ...PMS_DASHBOARDS.map((name): RenamedKey => ({from: `pms:${name}`, to: `pms:${name}:baseline`})),

    // ── the app's own keys, brought onto `<scope>:<name>` ────────────────────────────────────────────
    // App-wide settings and selections get the `ims:` scope; everything else names its backend.
    {from: 'app-config', to: 'ims:config'},
    // The three fund selections are written to BOTH buckets and read session-first: the session copy is
    // this tab's, the local one is the seed a new tab inherits (`funds.service.ts`).
    {from: 'selected-funds', to: 'ims:funds', buckets: ['local', 'session']},
    {from: 'pms-selected-fund', to: 'pms:fund', buckets: ['local', 'session']},
    {from: 'oms-selected-fund', to: 'oms:fund', buckets: ['local', 'session']},
    {from: 'defi-selected-fund', to: 'oms:defi:fund', buckets: ['local', 'session']},
    {from: 'rms-selected-fund', to: 'rms:fund', buckets: ['local', 'session']},
    // The only key that was on a dotted name rather than colon-separated.
    {from: 'ims.rms.selectedSimulation', to: 'rms:simulation'},
    {from: 'crm-currency', to: 'crm:currency'},
    {from: 'crm-series-filter', to: 'crm:series-filter'},
    {from: 'crm-investor-cols', to: 'crm:investors:columns'},
    // crm/investors keeps an implicit layout autosave in sessionStorage (`persistGridState`), which is
    // the one live user of the name this sweep drops from localStorage — see DEAD_KEYS.
    {from: 'crm-investors-table', to: 'crm:investors:state', buckets: ['session']},
    {from: 'hft-dashboard-layout-v1', to: 'hft:layout'},
    {from: 'futures-v2:currency', to: 'oms:futures:currency'},
];

/**
 * A payload assembled from the sub-keys a page used to write one by one. Unlike a rename, the shape
 * changes, so each one brings its own builder.
 */
interface SeededPayload {
    /** Where the assembled payload goes. */
    target: string;
    /** The keys it is built from, removed once it is in place. */
    sources: string[];
    /** Assemble it, or return null when there is nothing worth carrying. */
    build: (read: <T>(key: string) => T | null) => unknown | null;
}

/** `<base>:<sub>`, the shape those pages wrote one field at a time. */
function sub(base: string, name: string): string {
    return `${base}:${name}`;
}

/**
 * The prices and futures tables persisted every edit as it happened, one key per field. They are presets
 * hosts like everything else now, so what they had becomes their baseline — without it, everyone who
 * never saved a preset would open those pages empty.
 */
const PRICES_BASE = 'prices-v2-table';
const FUTURES_BASE = 'futures-v2-table';

const SEEDED_PAYLOADS: SeededPayload[] = [
    {
        target: 'oms:prices:baseline',
        sources: [
            sub(PRICES_BASE, 'selected-rows'), sub(PRICES_BASE, 'selected-assets'),
            sub(PRICES_BASE, 'selected-futures-periods'), sub(PRICES_BASE, 'selected-futures-value-groups'),
            sub(PRICES_BASE, 'pinned-rows'), sub(PRICES_BASE, 'aggregate'),
            sub(PRICES_BASE, 'inversion'), sub(PRICES_BASE, 'inverted'),
            sub(PRICES_BASE, 'dailyRateAdjusted'), sub(PRICES_BASE, 'futures-by-asset'),
            sub(PRICES_BASE, 'version'),
        ],
        build: read => {
            const rows = read<{pinOrder?: number}[]>(sub(PRICES_BASE, 'selected-rows'));
            const assets = read<string[]>(sub(PRICES_BASE, 'selected-assets'));
            if (!rows && !assets) return null;
            return {
                // The pin order rides along inside each row, as it does in the payload.
                selectedRows: rows ?? [],
                pinnedRows: (rows ?? []).filter(r => r?.pinOrder !== undefined),
                selectedBaseAssets: assets ?? [],
                selectedFuturesPeriods: read<string[]>(sub(PRICES_BASE, 'selected-futures-periods')) ?? [],
                selectedFuturesValueGroups:
                    read<unknown[]>(sub(PRICES_BASE, 'selected-futures-value-groups')) ?? [],
                aggregate: read<number>(sub(PRICES_BASE, 'aggregate')) ?? 0,
                inversion: read<number>(sub(PRICES_BASE, 'inversion')) ?? 0,
                inverted: read<boolean>(sub(PRICES_BASE, 'inverted')) ?? false,
                dailyRateAdjusted: read<boolean>(sub(PRICES_BASE, 'dailyRateAdjusted')) ?? false,
            };
        },
    },
    {
        target: 'oms:futures:baseline',
        sources: [sub(FUTURES_BASE, 'futures-by-asset'), sub(FUTURES_BASE, 'version')],
        build: read => {
            const byAsset = read<Record<string, unknown>>(sub(FUTURES_BASE, 'futures-by-asset'));
            return byAsset ? {byAsset} : null;
        },
    },
];

/**
 * Values that moved *inside* another payload rather than to a key of their own: a page choice that
 * became part of a preset. Unlike a rename the destination already exists, so they are merged in and
 * only then dropped.
 *
 * A fold is only performed when the destination can hold it. Where the payload's shape cannot be
 * invented from here — a PMS Dashboard stamps its Layout's `settingsVersion`, which lives in the Layout
 * — the keys are left alone and the owner's own read-forward carries them. That is the rule this whole
 * file follows: **a key is dropped when its value is provably somewhere else, never because enough time
 * has passed.**
 */
interface FoldedKeys {
    /** The payload to fold into. */
    target: string;
    /** Where inside it the values go (`page` for a grid host, `filters` for a Dashboard). */
    section?: string;
    /** Field in that section → the key it used to own, and how to read that key's raw string. */
    fields: Record<string, {key: string; read: (raw: string) => unknown}>;
    /** Build the payload when the target has none. Omitted when its shape cannot be invented here. */
    create?: () => Record<string, unknown>;
}

const asJson = (raw: string): unknown => {
    try {
        return JSON.parse(raw);
    } catch {
        return undefined;
    }
};
const asText = (raw: string): unknown => raw;
const asBool = (raw: string): unknown => raw === 'true';

const FOLDED_KEYS: FoldedKeys[] = [
    {
        // funding's page choices, contributed to its preset by the page and by each card.
        target: 'oms:funding:baseline',
        section: 'page',
        // The version a grid host stamps when nothing else says otherwise (`GRID_PRESET_VERSION`);
        // funding does not override it.
        create: () => ({version: 1, grids: {}}),
        fields: {
            rateView: {
                key: 'oms:funding:rate-view',
                read: raw => (raw === 'annualized' ? 'annualized' : 'daily'),
            },
            sectionsCollapsed: {key: 'oms:funding:sections-collapsed', read: asJson},
            'offers:showOnlyOffers': {key: 'oms:funding:offers:show-only-offers', read: asBool},
            'offers-history:executedOnly': {key: 'oms:funding:offers-history:executed-only', read: asBool},
            'taken:activeOnly': {key: 'oms:funding:taken:active-only', read: asBool},
            'provided:activeOnly': {key: 'oms:funding:provided:active-only', read: asBool},
        },
    },
    {
        // What the funding page is looking at: two keys became one, deliberately outside the preset.
        target: 'oms:funding:selection',
        create: () => ({}),
        fields: {
            currency: {key: 'oms:funding:currency', read: asText},
            account: {key: 'oms:funding:account', read: asText},
        },
    },
    {
        // The pricer's three. No `create`: the payload carries the Layout's settingsVersion, so a
        // Dashboard that never stored a baseline keeps its keys and the Layout reads them forward.
        target: 'pms:options-volatility:baseline',
        section: 'filters',
        fields: {
            pricer: {key: 'pms:options-volatility:pr', read: asJson},
            overrides: {key: 'pms:options-volatility:ov', read: asJson},
            showChart: {key: 'pms:options-volatility:showChart', read: raw => raw !== '0'},
        },
    },
];

/**
 * Keys nothing has read for releases.
 *
 * NOT the place for a key a host still reads forward on open: this runs at bootstrap, long before any
 * page mounts, so dropping one here would delete it out from under the fallback that was meant to carry
 * it. The keys that moved *into* another payload are handled by {@link FOLDED_KEYS} instead, which
 * merges the value first and drops the key only once it is provably there — so nothing here depends on
 * when a user last opened the app.
 */
const DEAD_KEYS = [
    // p-table `stateKey`s from pages that have since been rebuilt on AG Grid under another name.
    // `crm-investors-table` is the localStorage leftover of that era ONLY: crm/investors still writes
    // the same name in sessionStorage (`persistGridState(..., {storage: 'session'})`), which this sweep
    // does not touch. Should this ever grow a sessionStorage pass, that key must not come with it.
    'crm-investors-table',
    'tt-options-arbitrages-table',
    'options-arbitrages-table',
    // dashboard-cex's working state moved to sessionStorage; this is the stale localStorage copy of the
    // same name, and only the localStorage one (`clearWorkingState` owns the live key).
    'oms:dashboard-cex:working',
];

/**
 * Whole families of dead keys. `p-table-state-` is PrimeNG's own state prefix from the p-table era;
 * `dashboard-cex-` is that page's pre-envelope per-field storage, whose exact names varied by build
 * (fund-suffixed `dashboard-cex-starred:<fund>`, `dashboard-cex-quick-filter-settings`, …), which is why
 * it is matched by prefix rather than listed.
 */
const DEAD_PREFIXES = ['p-table-state-', 'dashboard-cex-'];

/**
 * Live keys that match a dead prefix and must survive. Empty on purpose: the one entry it used to hold
 * (`dashboard-cex-selection`) was renamed into its namespace instead — a live key that needs protecting
 * from a prefix sweep is a key whose name is wrong. Kept as the mechanism for the next dead prefix.
 */
const DEAD_PREFIX_KEEP = new Set<string>();

/** Run the sweep, once per browser. Safe to call on every startup. */
export function sweepLegacyStorage(): void {
    if (typeof localStorage === 'undefined' || typeof sessionStorage === 'undefined') return;
    if (alreadySwept()) return;

    let complete = true;
    for (const host of GRID_HOSTS) complete = step(() => migrateGridHost(host)) && complete;
    for (const rename of RENAMED_KEYS) complete = step(() => migrateRenamedKey(rename)) && complete;
    for (const seed of SEEDED_PAYLOADS) complete = step(() => seedPayload(seed)) && complete;
    // After the seeds: funding's payload may have just been assembled from its six table-state keys, and
    // a fold has to merge into it rather than replace it.
    for (const fold of FOLDED_KEYS) complete = step(() => foldKeys(fold)) && complete;
    complete = step(dropDeadKeys) && complete;

    // Only a clean pass is marked. A failed one leaves the legacy keys in place — the hosts still read
    // them — and is retried on the next load.
    if (complete) localStorage.setItem(SWEEP_KEY, String(SWEEP_VERSION));
}

function alreadySwept(): boolean {
    const done = Number(localStorage.getItem(SWEEP_KEY));
    return Number.isFinite(done) && done >= SWEEP_VERSION;
}

/**
 * One unit of work, isolated: a key that throws must not take the rest of the sweep with it, and a unit
 * that could not finish must not let the sweep mark itself done.
 */
function step(unit: () => boolean): boolean {
    try {
        return unit();
    } catch {
        return false;
    }
}

/** True when this host has nothing left to carry — either it never had, or the payload now holds it. */
function migrateGridHost(host: GridHostMigration): boolean {
    const legacyKeys = Object.values(host.slots)
        .flatMap(keys => [keys.state, keys.columns])
        .filter((key): key is string => !!key);
    if (!legacyKeys.some(key => localStorage.getItem(key) !== null)) return true;

    // A payload already there wins: the host has been used since the release, and its state is newer
    // than anything the legacy pair holds.
    if (!readBaseline(host.storageKey)) {
        const grids: Record<string, GridSlotState> = {};
        let version: number | undefined;
        for (const [slotId, keys] of Object.entries(host.slots)) {
            const legacy = readLegacySlot(keys);
            if (!legacy) continue;
            grids[slotId] = legacy.slot;
            // Every slot of a host was written by the same build, so the first stamp found speaks for
            // all of them. An unstamped payload is version 1, which is what the hosts assume too.
            version ??= legacy.version;
        }
        if (!Object.keys(grids).length) return true;
        const payload: GridPresetState = {version: version ?? 1, grids};
        writeBaseline(host.storageKey, payload);
        // Proof before removal: a write that silently failed (quota, a locked-down browser) must not
        // cost the layout it was carrying.
        if (!readBaseline(host.storageKey)) return false;
    }
    for (const key of legacyKeys) localStorage.removeItem(key);
    return true;
}

/** True when nothing is left to carry: `from` was empty, or `to` now holds its content. */
function migrateRenamedKey(rename: RenamedKey): boolean {
    let complete = true;
    for (const bucket of rename.buckets ?? ['local']) {
        const store = bucket === 'session' ? sessionStorage : localStorage;
        const raw = store.getItem(rename.from);
        if (raw === null) continue;

        // A value already at the destination is newer: it was written since the release.
        if (store.getItem(rename.to) === null) {
            store.setItem(rename.to, raw);
            if (store.getItem(rename.to) !== raw) {
                complete = false;
                continue;
            }
        }
        store.removeItem(rename.from);
    }
    return complete;
}

/** True when nothing is left to carry for this payload. */
function seedPayload(seed: SeededPayload): boolean {
    if (!seed.sources.some(key => localStorage.getItem(key) !== null)) return true;

    // A payload already there is newer: the page has been used since the release.
    if (localStorage.getItem(seed.target) === null) {
        const read = <T>(key: string): T | null => {
            const raw = localStorage.getItem(key);
            if (raw === null) return null;
            try {
                return JSON.parse(raw) as T;
            } catch {
                return null;
            }
        };
        const payload = seed.build(read);
        if (!payload) return true;
        localStorage.setItem(seed.target, JSON.stringify(payload));
        if (localStorage.getItem(seed.target) === null) return false;
    }
    for (const key of seed.sources) localStorage.removeItem(key);
    return true;
}

/** True when nothing is left to fold — the keys were absent, already folded, or cannot be yet. */
function foldKeys(fold: FoldedKeys): boolean {
    const sources = Object.values(fold.fields);
    if (!sources.some(source => localStorage.getItem(source.key) !== null)) return true;

    const raw = localStorage.getItem(fold.target);
    let payload: Record<string, unknown> | null = null;
    if (raw !== null) {
        const parsed = asJson(raw);
        // A destination we cannot read is not one to overwrite: leave both sides alone.
        if (!parsed || typeof parsed !== 'object') return true;
        payload = parsed as Record<string, unknown>;
    } else if (fold.create) {
        payload = fold.create();
    }
    if (!payload) return true;

    const section = fold.section
        ? {...(payload[fold.section] as Record<string, unknown> | undefined ?? {})}
        : payload;
    for (const [field, source] of Object.entries(fold.fields)) {
        const rawValue = localStorage.getItem(source.key);
        if (rawValue === null) continue;
        // A value already in the payload was written since the fold shipped, so it is the newer one.
        if (section[field] !== undefined) continue;
        const value = source.read(rawValue);
        if (value === undefined) continue;
        section[field] = value;
    }
    if (fold.section) payload[fold.section] = section;

    const serialized = JSON.stringify(payload);
    localStorage.setItem(fold.target, serialized);
    // Proof before removal, as everywhere here.
    if (localStorage.getItem(fold.target) !== serialized) return false;
    for (const source of sources) localStorage.removeItem(source.key);
    return true;
}

function dropDeadKeys(): boolean {
    for (const key of DEAD_KEYS) localStorage.removeItem(key);

    const matched: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (!key || DEAD_PREFIX_KEEP.has(key)) continue;
        if (DEAD_PREFIXES.some(prefix => key.startsWith(prefix))) matched.push(key);
    }
    for (const key of matched) localStorage.removeItem(key);
    return true;
}
